'use client';

import { useCallback, useEffect, useState } from 'react';
import { Mail, X, Check } from 'lucide-react';
import { safeStorageGet, safeStorageSet } from '@/lib/ui/safeStorage';

/**
 * Inline email capture on the cart page, for GUESTS only.
 *
 * WHY THIS EXISTS — none of the three existing capture points sit on the path
 * that actually creates a cart:
 *   • the popup is deliberately suppressed on /products/configure, /cart and
 *     /checkout (a paid ad click landed on the configurator and got the popup
 *     stacked on the cookie banner — it cost more sales than it won)
 *   • the checkout blur capture fires AFTER the cart, so anyone who abandons
 *     before checkout is never asked
 *   • "Email me this quote" in the configurator is opt-in
 *
 * So the journey  configure → add to cart → leave  produced an anonymous cart
 * every time: carts 79-84 all had "no email captured" against a real
 * configured shade. This asks once, at the one moment the visitor has both
 * intent and attention, without interrupting anything.
 *
 * It is a prompt, never a gate — the cart works identically if it is ignored.
 *
 * The address is POSTed to /api/email-subscribe, which stamps the `sessionId`
 * cookie onto the row. `carts.session_id` uses that same value, which is what
 * lets /admin/carts and the abandoned-cart job resolve the cart to a person.
 */

/** Set by EmailCapturePopup too — one capture per visitor is enough. */
const GUEST_EMAIL_KEY = 'guest_email';
const DISMISS_KEY = 'cart_email_capture_dismissed';

// safeStorage: never throws, and mirrors in memory when the browser refuses to
// persist — so a visitor with site data blocked is asked once, not on every
// page. Same store the popups use, so a capture there suppresses this too.
const read = (k: string): string | null => safeStorageGet(k);
const write = (k: string, v: string) => safeStorageSet(k, v);

export interface CartEmailCaptureProps {
  /** Recorded on email_subscribers.source so each placement can be measured
   *  separately. The /cart placement captured once in a week; the add-to-cart
   *  placement exists because that is where buyers actually are. */
  source?: string;
  heading?: string;
  subheading?: string;
  /** Extra classes on the outer panel, for placement-specific spacing. */
  className?: string;
}

export default function CartEmailCapture({
  source = 'cart',
  heading = 'Want your cart emailed to you?',
  subheading = 'Your sizes and options, saved — come back to them any time. No account needed.',
  className = '',
}: CartEmailCaptureProps = {}) {
  // 'checking' keeps the block out of the first paint, so it never flashes for
  // a signed-in customer who is already reachable.
  const [state, setState] = useState<'checking' | 'hidden' | 'open' | 'saved'>('checking');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      // Already captured (here or via the popup), or previously dismissed.
      if (read(GUEST_EMAIL_KEY) || read(DISMISS_KEY)) {
        if (!cancelled) setState('hidden');
        return;
      }
      try {
        // Same check the checkout page uses: a non-OK response means guest.
        const res = await fetch('/api/v2/auth/me', { credentials: 'include' });
        if (!cancelled) setState(res.ok ? 'hidden' : 'open');
      } catch {
        // Network failure — assume guest rather than lose the capture.
        if (!cancelled) setState('open');
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const dismiss = useCallback(() => {
    write(DISMISS_KEY, Date.now().toString());
    setState('hidden');
  }, []);

  const submit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setError('Please enter a valid email address');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch('/api/email-subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: value, source }),
      });
      if (!res.ok) throw new Error('save failed');
      write(GUEST_EMAIL_KEY, value);
      setState('saved');
    } catch {
      setError('Could not save that just now — you can still check out as normal.');
    } finally {
      setSubmitting(false);
    }
  }, [email, source]);

  if (state === 'checking' || state === 'hidden') return null;

  if (state === 'saved') {
    return (
      <div className="mb-4 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
        <Check className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <span>Saved — we&rsquo;ll email your cart so you can pick it up any time.</span>
      </div>
    );
  }

  return (
    <div className={`relative mb-4 rounded-lg border border-sb-border bg-sb-bg-alt p-4 ${className}`}>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute right-2 top-2 rounded p-1 text-gray-400 transition-colors hover:bg-white hover:text-gray-600"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex items-start gap-3">
        <Mail className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-red" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-heading-charcoal">{heading}</p>
          <p className="mt-0.5 text-xs text-gray-600">{subheading}</p>

          <form onSubmit={submit} className="mt-2.5 flex flex-col gap-2 sm:flex-row">
            <label htmlFor={`cart-capture-email-${source}`} className="sr-only">Email address</label>
            <input
              id={`cart-capture-email-${source}`}
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }}
              placeholder="you@example.com"
              autoComplete="email"
              className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-red focus:outline-none focus:ring-1 focus:ring-primary-red"
            />
            <button
              type="submit"
              disabled={submitting}
              className="rounded-lg bg-primary-red px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-dark disabled:opacity-60"
            >
              {submitting ? 'Saving…' : 'Email my cart'}
            </button>
          </form>

          {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
        </div>
      </div>
    </div>
  );
}
